import React from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./DayAppointmentsModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import { getAppointmentColors } from "../../utils/fundingSourceColors";
import ActionReportBadge from "../ActionReportBadge/ActionReportBadge";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

interface IDayAppointmentsModalProps {
  date: string; // ISO date string (YYYY-MM-DD)
  appointments: IAppointment[];
  onClose: () => void;
  onAppointmentClick: (appointment: IAppointment) => void;
}

/**
 * Day Appointments Modal Component
 *
 * Shows all appointments for a specific day when clicking "more appointments" in monthly view.
 * Displays appointments in a scrollable list with full details.
 */
const DayAppointmentsModal: React.FC<IDayAppointmentsModalProps> = ({
  date,
  appointments,
  onClose,
  onAppointmentClick,
}) => {
  const formatDate = (dateStr: string): string => {
    // Parse date string without timezone conversion issues
    const [year, month, day] = dateStr.split("-").map(Number);
    // Create date at noon to avoid timezone issues
    const dateObj = new Date(year, month - 1, day, 12, 0, 0);
    return dateObj.toLocaleDateString(getLocale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Sort appointments by time
  const sortedAppointments = [...appointments].sort((a, b) => {
    if (!a.time || !b.time) return 0;
    return a.time.localeCompare(b.time);
  });

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span>📅</span>
            <span>{formatDate(date)}</span>
          </div>
          <div className={styles.appointmentsCount}>
            {t("APPOINTMENTS_TOTAL_APPOINTMENTS", {
              count: appointments.length,
            })}
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {sortedAppointments.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>📭</span>
              <p>{t("APPOINTMENTS_NO_APPOINTMENTS_FOR_DAY")}</p>
            </div>
          ) : (
            <div className={styles.appointmentsList}>
              {sortedAppointments.map((apt) => {
                const fundingColor = getAppointmentColors(apt);
                const isOther = !apt.patientName && !apt.treatmentGroupName && !apt.mediaId;

                return (
                  <div
                    key={apt.id}
                    className={styles.appointmentItem}
                    onClick={() => onAppointmentClick(apt)}
                    style={{
                      borderRightColor: fundingColor.borderColor,
                      borderRightWidth: "3px",
                      backgroundColor: fundingColor.backgroundColor,
                    }}
                  >
                    <div className={styles.appointmentTime}>
                      <span className={styles.timeIcon}>🕐</span>
                      <span className={styles.time}>{apt.time}</span>
                      {apt.endTime && (
                        <span className={styles.endTime}>- {apt.endTime}</span>
                      )}
                    </div>

                    <div className={styles.appointmentDetails}>
                      {/* Show based on appointment type */}
                      {apt.treatmentGroupName ? (
                         <div className={styles.appointmentRow}>
                           <span className={styles.icon}>👥</span>
                           <span className={styles.label}>
                             {t("APPOINTMENTS_TREATMENT_GROUP")}:
                           </span>
                           <span
                             className={styles.value}
                             style={{ color: "#10B981", fontWeight: 500 }}
                           >
                             {apt.treatmentGroupName}
                           </span>
                         </div>
                       ) : isOther ? (
                         <div className={styles.appointmentRow}>
                           <span className={styles.icon}>📝</span>
                           <span className={styles.label}>
                             {t("MESSAGE_CONTENT") || "תוכן ההודעה"}:
                           </span>
                           <span className={styles.value}>
                             {apt.comments}
                           </span>
                         </div>
                       ) : (
                         <div className={styles.appointmentRow}>
                           <span className={styles.icon}>👤</span>
                           <span className={styles.label}>
                             {t("APPOINTMENTS_PATIENT")}:
                           </span>
                           <span className={styles.value}>
                             {apt.patientName}
                           </span>
                         </div>
                       )}
 
                       {/* Show therapist row only if there is a patient, group or media */}
                       {!isOther && (
                         <div className={styles.appointmentRow}>
                           <span className={styles.icon}>👨‍⚕️</span>
                           <span className={styles.label}>
                             {t("APPOINTMENTS_THERAPIST")}:
                           </span>
                           <span className={styles.value}>
                             {apt.therapistName}
                           </span>
                         </div>
                       )}

                      {apt.fundingSourceName && (
                        <div className={styles.appointmentRow}>
                          <span className={styles.icon}>💰</span>
                          <span className={styles.label}>
                            {t("APPOINTMENTS_FUNDING")}:
                          </span>
                          <span
                            className={styles.fundingBadge}
                            style={{
                              backgroundColor: fundingColor.badgeBg,
                              color: fundingColor.textColor,
                            }}
                          >
                            {apt.fundingSourceName}
                          </span>
                        </div>
                      )}

                      {apt.mediaName && (
                        <div className={styles.appointmentRow}>
                          <span className={styles.icon}>📍</span>
                          <span className={styles.label}>
                            {t("APPOINTMENTS_MEDIA")}:
                          </span>
                          <span className={styles.value}>{apt.mediaName}</span>
                        </div>
                      )}
                    </div>

                    {(apt.hasActionReport || apt.hasTherapistAnswered) && (
                      <div style={{ marginInlineStart: "auto", alignSelf: "center", flexShrink: 0, display: "flex", alignItems: "center", gap: 4 }}>
                        {apt.hasActionReport && <ActionReportBadge variant="check" size={16} />}
                        {apt.hasTherapistAnswered && <ActionReportBadge variant="report" size={16} />}
                      </div>
                    )}
                    <div className={styles.appointmentArrow}>→</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.closeButton} onClick={onClose}>
            {t("APPOINTMENTS_CLOSE")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayAppointmentsModal;
